import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { CreditCard, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { initializePaddle, getPaddlePriceId } from "@/lib/paddle";
import { PaymentTestModeBanner } from "./PaymentTestModeBanner";

interface PaddleBudgetPurchaseProps {
  currentBudget: number;
  onBudgetUpdate?: (newBudget: number) => void;
}

const PACKAGES = [
  { priceId: "topup_5_price", amount: 5, label: "מתחיל" },
  { priceId: "topup_10_price", amount: 10, label: "בסיסי" },
  { priceId: "topup_25_price", amount: 25, label: "מקצועי", popular: true },
  { priceId: "topup_50_price", amount: 50, label: "פרימיום" },
  { priceId: "topup_100_price", amount: 100, label: "ארגוני" },
];

export function PaddleBudgetPurchase({ currentBudget, onBudgetUpdate }: PaddleBudgetPurchaseProps) {
  const { user } = useAuth();
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const pollForBudget = async (initialBudget: number, addedAmount: number) => {
    // Poll for up to ~20s for the webhook to update the budget.
    for (let i = 0; i < 20; i++) {
      await new Promise((r) => setTimeout(r, 1000));
      const { data } = await supabase
        .from("profiles")
        .select("budget")
        .eq("id", user!.id)
        .single();
      const newBudget = parseFloat(String(data?.budget ?? 0));
      if (newBudget >= initialBudget + addedAmount - 0.001) {
        onBudgetUpdate?.(newBudget);
        toast.success(`נוספו $${addedAmount} לחשבונך 🎉`);
        return;
      }
    }
    toast.info("התשלום התקבל. עדכון היתרה עשוי לקחת מספר רגעים.");
  };

  const handleBuy = async (priceId: string, amount: number) => {
    if (!user) {
      toast.error("יש להתחבר תחילה");
      return;
    }
    setSelected(priceId);
    setLoading(true);
    try {
      await initializePaddle();
      const paddlePriceId = await getPaddlePriceId(priceId);

      window.Paddle.Checkout.open({
        items: [{ priceId: paddlePriceId, quantity: 1 }],
        customer: user.email ? { email: user.email } : undefined,
        customData: { userId: user.id, creditAmount: String(amount) },
        settings: {
          displayMode: "overlay",
          successUrl: `${window.location.origin}/dashboard?topup=success`,
          allowLogout: false,
          variant: "one-page",
        },
        eventCallback: (ev: any) => {
          if (ev?.name === "checkout.completed") {
            toast.success("התשלום התקבל, מעדכן יתרה...");
            pollForBudget(currentBudget, amount);
          }
          if (ev?.name === "checkout.closed") {
            setLoading(false);
          }
        },
      });
    } catch (e) {
      console.error(e);
      toast.error("שגיאה בפתיחת החיוב");
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="w-5 h-5" />
          רכישת תקציב
        </CardTitle>
        <CardDescription>
          בחר חבילת תקציב — הסכום יתווסף ליתרה שלך מיד לאחר התשלום.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <PaymentTestModeBanner />

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {PACKAGES.map((pkg) => {
            const isSelected = selected === pkg.priceId;
            return (
              <div
                key={pkg.priceId}
                className={`relative p-4 rounded-lg border-2 transition-all ${
                  pkg.popular ? "border-primary" : "border-border"
                }`}
              >
                {pkg.popular && (
                  <Badge className="absolute -top-2 right-3">הכי פופולרי</Badge>
                )}
                <div className="text-sm text-muted-foreground">{pkg.label}</div>
                <div className="text-3xl font-bold my-2">${pkg.amount}</div>
                <div className="text-xs text-muted-foreground mb-4">
                  ${pkg.amount} יתווספו ליתרה
                </div>
                <Button
                  className="w-full"
                  disabled={loading}
                  onClick={() => handleBuy(pkg.priceId, pkg.amount)}
                >
                  {loading && isSelected ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 ml-2" />
                      רכוש עכשיו
                    </>
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
